import {
  N8N_WEBHOOK_URL,
  PANEL_FETCH_CREDENTIALS,
  panelAuthHeaders,
} from "../config";
import { fetchWithTransientRetry, HttpStatusError } from "./api/transientRetry";
import { rememberStoreLogo } from "../utils/storeLogoCache";

export type KitchenStore = {
  cnpj: string;
  nome: string;
  short_label: string;
  letter: string;
  status?: string;
  current?: boolean;
  bot_on?: boolean;
  logo_url?: string;
};

export type KitchenStatus = {
  ok?: boolean;
  linked?: boolean;
  incoming?: boolean;
  email?: string;
  stores?: KitchenStore[];
  group_license_cents?: number;
  group_license_stores?: number;
  dark_kitchen_plan?: boolean;
  error?: string;
};

function kitchenUrl(path: string, cnpj: string, extra?: Record<string, string>): string {
  const q = new URLSearchParams({ cnpj: cnpj.replace(/\D/g, ""), ...(extra || {}) });
  return `${N8N_WEBHOOK_URL}dark-kitchen/${path}?${q.toString()}`;
}

async function postKitchen(
  path: string,
  cnpj: string,
  body?: Record<string, unknown>
): Promise<KitchenStatus> {
  const res = await fetch(kitchenUrl(path, cnpj), {
    method: "POST",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body || {}),
  });
  const data = (await res.json().catch(() => ({}))) as KitchenStatus;
  if (!res.ok && !data.error) {
    return { ok: false, error: "kitchen_failed" };
  }
  return data;
}

export const KITCHEN_SWITCH_EVENT = "delivai-kitchen-switch";

export async function applyKitchenSession(cnpj: string, nome?: string) {
  const digits = cnpj.replace(/\D/g, "");
  const current = (localStorage.getItem("cnpj") || "").replace(/\D/g, "");
  if (current && current !== digits) {
    try {
      await switchKitchenStore(current, digits);
    } catch {
      /* a sessão da cozinha ainda alcança a outra loja pelo CNPJ */
    }
  }
  localStorage.setItem("cnpj", digits);
  try {
    const raw = localStorage.getItem("delivai_user");
    if (raw) {
      const user = JSON.parse(raw) as Record<string, unknown>;
      user.cnpj = digits;
      if (nome) user.name = nome;
      localStorage.setItem("delivai_user", JSON.stringify(user));
    }
  } catch {
    /* sessão segue pelo cookie */
  }
  window.dispatchEvent(
    new CustomEvent(KITCHEN_SWITCH_EVENT, { detail: { cnpj: digits, nome } })
  );
}

export async function fetchKitchenStatus(cnpj: string): Promise<KitchenStatus> {
  const res = await fetch(kitchenUrl("status", cnpj), {
    method: "GET",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders(),
  });
  const data = (await res.json().catch(() => ({ ok: false }))) as KitchenStatus;
  for (const store of data.stores || []) {
    rememberStoreLogo(store.cnpj, store.logo_url);
  }
  return data;
}

export type KitchenLoginStore = {
  nome: string;
  short_label?: string;
  logo_url?: string;
};

export async function checkKitchenEmail(email: string): Promise<{
  exists: boolean;
  stores: KitchenLoginStore[];
}> {
  const res = await fetch(`${N8N_WEBHOOK_URL}dark-kitchen/check-email`, {
    method: "POST",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ email: email.trim() }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    exists?: boolean;
    stores?: KitchenLoginStore[];
  };
  return {
    exists: data.exists === true,
    stores: Array.isArray(data.stores) ? data.stores : [],
  };
}

export async function loginKitchen(body: {
  email: string;
  password: string;
  cnpj?: string;
}): Promise<{
  ok?: boolean;
  authenticated?: boolean;
  cnpj?: string;
  nome?: string;
  error?: string;
  stores?: KitchenStore[];
}> {
  const res = await fetchWithTransientRetry(`${N8N_WEBHOOK_URL}dark-kitchen/login`, {
    method: "POST",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok && res.status !== 400) {
    throw new HttpStatusError(res.status, `kitchen login ${res.status}`);
  }
  let data: {
    ok?: boolean;
    authenticated?: boolean;
    cnpj?: string;
    nome?: string;
    error?: string;
    stores?: KitchenStore[];
  };
  try {
    data = (await res.json()) as typeof data;
  } catch {
    if (res.status === 400) return { ok: false, authenticated: false };
    throw new HttpStatusError(res.status || 502, "kitchen login json");
  }
  return data as {
    ok?: boolean;
    authenticated?: boolean;
    cnpj?: string;
    nome?: string;
    error?: string;
    stores?: KitchenStore[];
  };
}

export function inviteKitchen(
  storeCnpj: string,
  otherCnpj: string,
  credentials?: { email: string; password: string }
): Promise<KitchenStatus> {
  return postKitchen("invite", storeCnpj, {
    cnpj: otherCnpj,
    ...(credentials
      ? { email: credentials.email.trim().toLowerCase(), password: credentials.password }
      : {}),
  });
}

export async function suggestKitchenStores(
  storeCnpj: string,
  digits: string
): Promise<Array<{ cnpj: string; nome: string }>> {
  const q = digits.replace(/\D/g, "").slice(0, 14);
  if (q.length < 3) return [];
  const res = await fetch(kitchenUrl("suggest", storeCnpj, { q }), {
    method: "GET",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as {
    items?: Array<{ cnpj?: string; nome?: string }>;
  };
  return (data.items || [])
    .map((item) => ({
      cnpj: String(item.cnpj || "").replace(/\D/g, ""),
      nome: String(item.nome || "").trim(),
    }))
    .filter((item) => item.cnpj.length === 14 && item.nome);
}

export function updateKitchenEmail(
  storeCnpj: string,
  email: string,
  password?: string
): Promise<KitchenStatus> {
  return postKitchen("email", storeCnpj, {
    email: email.trim().toLowerCase(),
    ...(password ? { password } : {}),
  });
}

export function acceptKitchen(cnpj: string): Promise<KitchenStatus> {
  return postKitchen("accept", cnpj);
}

export function rejectKitchen(cnpj: string): Promise<KitchenStatus> {
  return postKitchen("reject", cnpj);
}

export function leaveKitchen(cnpj: string, targetCnpj?: string): Promise<KitchenStatus> {
  return postKitchen("leave", cnpj, targetCnpj ? { cnpj: targetCnpj } : {});
}

export async function switchKitchenStore(
  storeCnpj: string,
  targetCnpj: string
): Promise<KitchenStatus & { nome?: string; cnpj?: string }> {
  return postKitchen("switch", storeCnpj, { cnpj: targetCnpj }) as Promise<
    KitchenStatus & { nome?: string; cnpj?: string }
  >;
}

export async function fetchKitchenOrders(cnpj: string, day?: string): Promise<{
  ok?: boolean;
  pedidos?: unknown[];
}> {
  const extra = day ? { data: day } : undefined;
  const res = await fetch(kitchenUrl("orders", cnpj, extra), {
    method: "GET",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders(),
  });
  return (await res.json().catch(() => ({ ok: false }))) as { ok?: boolean; pedidos?: unknown[] };
}

export async function fetchKitchenCouriers(cnpj: string): Promise<
  Array<{ nome?: string; telefone?: string; cpf?: string; cargo?: unknown }>
> {
  const res = await fetch(kitchenUrl("couriers", cnpj), {
    method: "GET",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as {
    couriers?: Array<{ nome?: string; telefone?: string; cpf?: string; cargo?: unknown }>;
  };
  return Array.isArray(data.couriers) ? data.couriers : [];
}
